import { Input } from 'cd-eft/components/common/Input';
import { observer } from 'mobx-react';
import React, { Fragment, useEffect, useRef } from 'react';
import { Route } from 'react-router-dom';
import { FormulaBanner } from '../Formulas/FormulaComponents/Common/FormulaBanner';
import { FormulaPrintModalContainer } from '../Formulas/FormulaComponents/template/FormulaFormTemplates';
import { Row, Table, CustomInput } from 'reactstrap';
import { ComboBox } from 'cd-eft/components/common/ComboBox';
import './HydrologyCalculator.scss';
import { HydrologyCalculatorVM } from './HydrologyCalculatorVM';
import stores from 'cd-eft/models/Stores';
import { UnderConstruction } from 'cd-eft/components/common/UnderConstruction';
import { StaticTooltip } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';

const AppStore = stores.AppStore;

const HydrologyCalculatorHome = () => {
  return (
    <Fragment>
      <Route path={`/analysis/hydrology/hydrology-calculator`}>
        {AppStore.userProfileStore.applicationFlags.enablehydrocalc ? <HydrologyCalculator /> : <UnderConstruction />}
      </Route>
    </Fragment>
  );
};

export const HydrologyCalcHome = observer(HydrologyCalculatorHome);

const HydrologyCalculatorComponent = (props) => {
  const vmRef = useRef<HydrologyCalculatorVM>();
  if (!vmRef.current) {
    vmRef.current = new HydrologyCalculatorVM(props);
  }
  const vm = vmRef.current;

  useEffect(() => {
    vm.init();
  }, []);

  return (
    <Fragment>
      <FormulaBanner title={'Hydrology Calculator'} help={''} noFooter={true} />
      <div className="row hydrology-calculator-content">
        <div className="col hydro-calc-inputs">
          <Row>
            <label className={`${vm.headerSwitch ? 'header selected' : 'header'}`} htmlFor='useNRCS-rainfall-database'>
              Use NRCS Rainfall Database
              <StaticTooltip tooltipMessage="The NRCS Rainfall Database utilizes county-level 24-hour precipitation depth from
              NOAA Atlas 14 and NOAA Atlas 2 provided by each state. Some counties and/or states
              may have missing rainfall data. The NETS Hydrology Single Watershed which includes
              site-specific - watershed delineation, rainfall values and site-specific rainfall distribution
              using NOAA Atlas 14 data." tooltipSide='bottom' id='tooltip-01' />
            </label>
            <CustomInput
              id="useNRCS-rainfall-database"
              type="switch"
              className={`checkbox-input-switch${vm.headerSwitch ? ' selected' : ''}`}
              label={`${vm.headerSwitch ? 'Yes' : 'No'}`}
              checked={vm.headerSwitch}
              name="useNRCS-rainfall-database"
              bsSize="lg"
              onChange={vm.toggleHeaderSwitch}
            />
          </Row>
          <ComboBox
            idAndName="state"
            label={`State`}
            aria-label="State"
            valid={!vm.formValidation.state}
            value={vm.hydroCalculatorFormData.state}
            onChange={(e) => {
              vm.handleStateInputChange(e);
            }}
            onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
            options={vm.stateOptions.map((el) => ({
              name: el.state_name!,
              value: el.state_code!,
            }))}
            errorMsg={''}
            required={true}
            className={vm.hydroCalcErrorClass('state')}
          />
          {vm.headerSwitch ?
            <ComboBox
              label="County"
              idAndName="county"
              aria-label="County"
              valid={!vm.formValidation.county}
              value={vm.hydroCalculatorFormData.county}
              tooltipMessage="Select your State and County from the dropdown list using the NRCS Rainfall Database, or enter 
              the information manually. If you choose to use the rainfall database, the rainfall values and rainfall 
              distribution type will automatically be populated based on your selection of State and County.  
              The State and County will be included in the report generated."
              infoIconCallback={() => { }}
              onChange={(e) => {
                vm.handleInputChange(e);
              }}
              onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
              className={vm.hydroCalcErrorClass('county')}
              options={
                vm.countiesOptions.length > 0
                  ? vm.countiesOptions.map((el) => ({
                    name: el.county!,
                    value: el.id!,
                  }))
                  : []
              }
              errorMsg={''}
              required={true}
            />
            :
            <div className='longer-labels county'>
              <Input
                label="County"
                idAndName="county"
                aria-label="County"
                valid={!vm.formValidation.county}
                infoIcon={true}
                infoText="Select your State and County from the dropdown list using the NRCS Rainfall Database, or enter 
              the information manually. If you choose to use the rainfall database, the rainfall values and rainfall 
              distribution type will automatically be populated based on your selection of State and County. 
              The State and County will be included in the report generated."
                infoIconCallback={() => { }}
                value={vm.hydroCalculatorFormData.county}
                type="text"
                onChange={(e) => {
                  vm.handleInputChange(e);
                }}
                onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
                className={`${vm.hydroCalcErrorClass('county')} hydro-calc-input`}
                required={true}
              />
            </div>}
          <div className="longer-labels">
            <Input
              label="Drainage Area (ac)"
              idAndName="drainage-area"
              valid={!vm.formValidation.drainageArea}
              value={vm.hydroCalculatorFormData.drainageArea}
              type="number"
              onChange={(e) => {
                vm.handleInputChange(e);
              }}
              step={0.01}
              onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
              className={vm.hydroCalcErrorClass('drainageArea')}
              min={0.01}
              required={true}
            />
            <Input
              label="Runoff Curve Number"
              idAndName="runoff-curve-number"
              valid={!vm.formValidation.runoffCurveNumber}
              value={vm.hydroCalculatorFormData.runoffCurveNumber}
              type="number"
              onChange={(e) => {
                vm.handleInputChange(e);
              }}
              step={0.01}
              onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
              className={vm.hydroCalcErrorClass('runoffCurveNumber')}
              min={0.01}
              required={true}
            />
            <Input
              label={`Watershed Length (ft)${!vm.headerSwitch ? ' - Optional' : ''}`}
              idAndName="watershed-length"
              valid={!vm.formValidation.watershedLength}
              value={vm.hydroCalculatorFormData.watershedLength}
              type="number"
              onChange={(e) => {
                vm.handleInputChange(e);
              }}
              step={0.01}
              onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
              className={vm.hydroCalcErrorClass('watershedLength')}
              min={0.01}
              required={vm.headerSwitch}
            />
            <Input
              label={`Watershed Slope (%)${!vm.headerSwitch ? ' - Optional' : ''}`}
              idAndName="watershed-slope"
              valid={!vm.formValidation.watershedSlope}
              value={vm.hydroCalculatorFormData.watershedSlope}
              type="number"
              onChange={(e) => {
                vm.handleInputChange(e);
              }}
              step={0.01}
              onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
              className={vm.hydroCalcErrorClass('watershedSlope')}
              min={0.01}
              required={vm.headerSwitch}
            />
            <Input
              label="Time of Concentration (hrs)"
              idAndName="time-of-concentration"
              valid={!vm.formValidation.timeOfConcentration}
              value={vm.hydroCalculatorFormData.timeOfConcentration}
              type="number"
              onChange={(e) => {
                vm.handleInputChange(e);
              }}
              step={0.01}
              onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
              className={vm.hydroCalcErrorClass('timeOfConcentration')}
              min={0.01}
              readOnly={vm.headerSwitch}
              required={!vm.headerSwitch}
            />
            {vm.headerSwitch ?
              <Input
                idAndName="rainfall-distribution-type"
                label="Rainfall Distribution Type"
                aria-label="Rainfall Distribution Type"
                infoIcon={true}
                infoText="Select rainfall distribution developed using NOAA Atlas 14 data (NRCS Rainfall
            Database) based on State and County."
                infoIconCallback={() => { }}
                valid={!vm.formValidation.rainfallDistributionType}
                value={vm.hydroCalculatorFormData.rainfallDistributionType}
                type="string"
                onChange={() => { }}
                onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
                className={`${vm.hydroCalcErrorClass('rainfallDistributionType')} hydro-calc-input`}
                readOnly={true}
                required={true}
              />
              :
              <ComboBox
                idAndName="rainfall-distribution-type"
                label="Rainfall Distribution Type"
                aria-label="Rainfall Distribution Type"
                valid={!vm.formValidation.rainfallDistributionType}
                value={vm.hydroCalculatorFormData.rainfallDistributionType}
                tooltipMessage="Select rainfall distribution developed using NOAA Atlas 14 data (NRCS Rainfall
            Database) based on State and County."
                infoIconCallback={() => { }}
                onChange={(e) => {
                  vm.handleInputChange(e);
                }}
                onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
                options={vm.rainfallOptions}
                errorMsg={''}
                required={true}
                className={vm.hydroCalcErrorClass('rainfallDistributionType')}
              />
            }
            <ComboBox
              label="Dimensionless Unit Hydrograph"
              idAndName="dimensionless-unit-hydrograph"
              aria-label="Dimensionless Unit Hydrograph"
              valid={!vm.formValidation.dimensionlessUnitHydrograph}
              value={vm.hydroCalculatorFormData.dimensionlessUnitHydrograph}
              tooltipMessage='The Dimensionless Unit Hydrograph (DUH) used as an NRCS standard for many years has a peak rate factor of 484. 
              The NETS default is Standard-484.The shape of the DUH determines the peak rate factor. The higher the peak rate factor, 
              the higher the peak discharge will be (NEH Part 630 Hydrology, Chapter 16). It can be changed if necessary (consult 
              the State Conservation Engineer or the State Hydraulic Engineer for applicably of a specific DUH). The results must 
              be recalculated if the DUH is changed by selecting the Submit button at the lower left corner of the page.'
              infoIconCallback={() => { }}
              onChange={(e) => {
                vm.handleInputChange(e);
              }}
              onBlur={(e) => vm.validateInput(e.target.name, e.target.value)}
              className={vm.hydroCalcErrorClass('dimensionlessUnitHydrograph')}
              options={vm.dimUnitHydrogOptions.map((x) => {
                return { value: x.id, name: x.name };
              })}
              errorMsg={''}
              required={true}
            />
          </div>
        </div>

        <div className="col">
          <p>
            The Hydrology Calculator tool is used to estimate and analyze various hydrological parameters like peak discharge, runoff volume, and time of concentration. The
            calculator helps engineers and technicians to predict the behavior of water in a watershed (pre- and post- developed condition) for tasks like flood
            prediction, water resource management, land development and conservation practices. This procedure includes watershed characteristics (drainage
            area, curve number, watershed length, and watershed slope), rainfall amount and rainfall distribution.
          </p>
          <div className="row">
            {!vm.headerSwitch ? (
              <>
                <Table className={`usa-table table-container col-auto ${vm.resultsRows ? 'hresults' : ''}`}>
                  <thead>
                    <tr>
                      <th colSpan={2} className="input-header sections-heading selected">
                        Inputs <StaticTooltip tooltipMessage="The Hydrology Calculator will calculate the peak discharge (cfs) and runoff volume
                        (inch) for each row when the user enter the frequency and 24-hour rainfall value. Select
                        the “Calculate” button. As a user, you can select which frequencies (yr) you would like
                        to analyze. Currently, there is no option to delete a row; however, you can select
                        different frequency if needed." tooltipSide='right' id='tooltip-02' />
                      </th>
                      {vm.resultsRows ? (
                        <th colSpan={2} className="results-header selected sections-heading input-first-cell">
                          Results
                        </th>
                      ) : null}
                    </tr>
                    <tr>
                      <th className="input-subheader input-first-cell" id='input-frequency'>Frequency (yr)</th>
                      <th className="input-subheader" id='input-rainfall'>24-hr Rain (in)</th>
                      {vm.resultsRows ? (
                        <>
                          <th className="results-header input-first-cell">Peak Flow Q (cfs)</th>
                          <th className="results-header ">Runoff (in)</th>
                        </>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {vm.hydroCalculatorFormData.hydrologyCalculatorTableData.length > 0
                      ? vm.hydroCalculatorFormData.hydrologyCalculatorTableData.map((item, index) => (
                        <tr key={'hydroCalcTable-' + index}>
                          <td className="freq">
                            <ComboBox
                              aria-label={`table-inputs-freq-${index}`}
                              idAndName={`table-inputs-freq-${index}`}
                              value={item.freq}
                              onChange={(e) => vm.handleInputChange(e, index)}
                              options={vm.frequencyOptions}
                              valid={
                                vm.formValidation.hydrologyCalculatorTableData[index]
                                  ? !vm.formValidation.hydrologyCalculatorTableData[index].freq
                                  : true
                              }
                              errorMsg={''}
                              className={vm.hydroCalcErrorClass(`table-inputs-freq-${index}`)}
                              wrapperName="hydroCalc"
                              externalLabel='input-frequency'
                            />
                          </td>
                          <td className="rain">
                            <Input
                              aria-label={`table-inputs-rain-${index}`}
                              idAndName={`table-inputs-rain-${index}`}
                              type="number"
                              value={item.rain}
                              valid={
                                vm.formValidation.hydrologyCalculatorTableData[index]
                                  ? !vm.formValidation.hydrologyCalculatorTableData[index].rain
                                  : true
                              }
                              onChange={(e) => vm.handleInputChange(e, index)}
                              min={0.01}
                              step={0.01}
                              onKeyDown={vm.handleOnKeyDown}
                              onBlur={(e) => vm.roundRainValue(e, index)}
                              className={vm.hydroCalcErrorClass(`table-inputs-rain-${index}`)}
                              wrapperClassName="hydroCalc"
                              externalLabel='input-rainfall'
                            />
                          </td>
                          {vm.resultsRows && vm.resultsRowsData.length > 0 && index < vm.resultsRowsData.length ? (
                            <>
                              <td
                                className={`results-header hresultsbody input-left-cell ${index === vm.hydroCalculatorFormData.hydrologyCalculatorTableData.length - 1
                                  ? 'results-header-border'
                                  : ''
                                  }`}
                              >
                                {vm.resultsRowsData[index] && vm.resultsRowsData[index].peakFlowQ
                                  ? vm.resultsRowsData[index].peakFlowQ
                                  : ''}
                              </td>
                              <td
                                className={`results-header hresultsbody input-right-cell ${index === vm.hydroCalculatorFormData.hydrologyCalculatorTableData.length - 1
                                  ? 'results-header-border'
                                  : ''
                                  }`}
                              >
                                {vm.resultsRowsData[index] && vm.resultsRowsData[index].runoff
                                  ? vm.resultsRowsData[index].runoff
                                  : ''}
                              </td>
                            </>
                          ) : null}
                          {vm.resultsRows && vm.resultsRowsData.length > 0 && index === vm.resultsRowsData.length ? (
                            <>
                              <td
                                className={`results-header hresultsbody input-left-cell`}
                              >
                                {''}
                              </td>
                              <td
                                className={`results-header hresultsbody input-right-cell`}
                              >
                                {''}
                              </td>
                            </>
                          ) : null}
                        </tr>
                      ))
                      : null}
                  </tbody>
                </Table>
              </>
            ) : (
              <>
                {vm.resultsRows && vm.resultsRowsDataUseDatabase.length ? (
                  <Table className="usa-table table-container col hresults">
                    <thead>
                      <tr>
                        <th colSpan={4} className="results-header sections-heading hresultsbody selected">
                          Results
                        </th>
                      </tr>
                      <tr>
                        <th className="results-header">Frequency (yr)</th>
                        <th className="results-header">24-hr Rain</th>
                        <th className="results-header">Peak Flow Q (cfs)</th>
                        <th className="results-header">Runoff (in)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vm.resultsRowsDataUseDatabase.length > 0
                        ? vm.resultsRowsDataUseDatabase.map((item, index) => (
                          <tr key={'hydroCalcTable-' + index}>
                            <td className="results-header input-left-cell">{item.freq}</td>
                            <td className="results-header">{item.rain}</td>
                            <td className="results-header">{item.peakFlowQ}</td>
                            <td className="results-header input-right-cell">{item.runoff}</td>
                          </tr>
                        ))
                        : null}
                      <td
                        className="time-concentration input-left-cell input-right-cell results-header-border"
                        colSpan={4}
                      >
                        <b>Time of Concentration (hrs)</b>: {vm.displayToC}
                      </td>
                    </tbody>
                  </Table>
                ) : null}
              </>
            )}
            <span className=" span-buttons col">
              <button className="usa-button" data-testid="input-container-button" onClick={vm.calculateResults} disabled={vm.isCalculating}>
                Compute
              </button>
              <button
                className="usa-button"
                data-testid="input-container-button"
                disabled={!vm.resultsRows}
                onClick={vm.launchAndExport}
              >
                Print
              </button>
              <button
                className="usa-button usa-button--outline"
                data-testid="input-container-button"
                onClick={vm.reset}
              >
                Reset
              </button>
            </span>
          </div>
        </div>
        {vm.isCalculating && <Loader message={'Calculating'} />}
      </div>
      <FormulaPrintModalContainer
        vm={vm}
        inputs={vm.exportedInputs}
        results={vm.exportedResults}
        images={vm.image ? [vm.image] : []}
        formulaName={vm.formulaTitle}
        tableData={true}
      />
    </Fragment>
  );
};

export const HydrologyCalculator = observer(HydrologyCalculatorComponent);
